/* tslint:disable:directive-selector */

import {
  AfterContentInit,
  ContentChildren,
  Directive,
  EventEmitter,
  forwardRef,
  Input,
  OnChanges,
  OnDestroy,
  Output,
  QueryList,
  SimpleChange
} from '@angular/core';
import {Observable, ReplaySubject, Subscription} from 'rxjs';
import {ExgMapFitBoundsAccessor, ExgMapFitBoundsDetails, ExgMapMarkerManagerService} from '../services';
import {ExgMapInfoWindowComponent} from '../directives';

let markerId = 0;

@Directive({
  selector: 'exg-map-marker',
  providers: [
    {provide: ExgMapFitBoundsAccessor, useExisting: forwardRef(() => ExgMapMarkerDirective)},
  ],
})
export class ExgMapMarkerDirective implements OnDestroy, OnChanges, AfterContentInit, ExgMapFitBoundsAccessor {
  /**
   * The latitude position of the marker.
   */
  @Input() latitude: number | undefined;

  /**
   * The longitude position of the marker.
   */
  @Input() longitude: number | undefined;

  /**
   * The title of the marker.
   */
  @Input() title: string | undefined;

  /**
   * The label (a single uppercase character) for the marker.
   */
  @Input() label: string | google.maps.MarkerLabel | undefined;

  /**
   * If true, the marker can be dragged. Default value is false.
   */
    // tslint:disable-next-line:no-input-rename
  @Input('markerDraggable') draggable = false;

  /**
   * Icon (the URL of the image) for the foreground.
   */
  @Input() iconUrl: string | google.maps.Icon | google.maps.Symbol | undefined;

  /**
   * If true, the marker is visible
   */
  @Input() visible = true;

  /**
   * Whether to automatically open the child info window when the marker is clicked.
   */
  @Input() openInfoWindow = true;

  /**
   * The marker's opacity between 0.0 and 1.0.
   */
  @Input() opacity = 1;

  /**
   * All markers are displayed on the map in order of their zIndex, with higher values displaying in
   * front of markers with lower values. By default, markers are displayed according to their
   * vertical position on screen, with lower markers appearing in front of markers further up the
   * screen.
   */
  @Input() zIndex = 1;

  /**
   * If true, the marker can be clicked. Default value is true.
   */
    // tslint:disable-next-line:no-input-rename
  @Input('markerClickable') clickable = true;

  /**
   * Which animation to play when marker is added to a map.
   * This can be 'BOUNCE' or 'DROP'
   */
  @Input() animation: keyof typeof google.maps.Animation | undefined;

  /**
   * This event is fired when the marker's animation property changes.
   */
  @Output() animationChange = new EventEmitter<keyof typeof google.maps.Animation>();

  /**
   * This event emitter gets emitted when the user clicks on the marker.
   */
  @Output() markerClick: EventEmitter<ExgMapMarkerDirective> = new EventEmitter<ExgMapMarkerDirective>();

  /**
   * This event emitter gets emitted when the user clicks twice on the marker.
   */
  @Output() markerDblClick: EventEmitter<ExgMapMarkerDirective | null> = new EventEmitter<ExgMapMarkerDirective | null>();

  /**
   * This event is fired when the user rightclicks on the marker.
   */
  @Output() markerRightClick: EventEmitter<void | null> = new EventEmitter<void | null>();

  /**
   * This event is fired when the user starts dragging the marker.
   */
  @Output() dragStart: EventEmitter<google.maps.MapMouseEvent> = new EventEmitter<google.maps.MapMouseEvent>();

  /**
   * This event is repeatedly fired while the user drags the marker.
   */
    // tslint:disable-next-line: no-output-native
  @Output() drag: EventEmitter<google.maps.MapMouseEvent> = new EventEmitter<google.maps.MapMouseEvent>();

  /**
   * This event is fired when the user stops dragging the marker.
   */
  @Output() dragEnd: EventEmitter<google.maps.MapMouseEvent> = new EventEmitter<google.maps.MapMouseEvent>();

  /**
   * This event is fired when the user mouses over the marker.
   */
  @Output() mouseOver: EventEmitter<google.maps.MapMouseEvent> = new EventEmitter<google.maps.MapMouseEvent>();

  /**
   * This event is fired when the user mouses outside the marker.
   */
  @Output() mouseOut: EventEmitter<google.maps.MapMouseEvent> = new EventEmitter<google.maps.MapMouseEvent>();

  /** @internal */
  @ContentChildren(ExgMapInfoWindowComponent) infoWindow: QueryList<ExgMapInfoWindowComponent> = new QueryList<ExgMapInfoWindowComponent>();

  private _markerAddedToManger = false;
  private _id: string;
  private _observableSubscriptions: Subscription[] = [];

  protected readonly _fitBoundsDetails$: ReplaySubject<ExgMapFitBoundsDetails> = new ReplaySubject<ExgMapFitBoundsDetails>(1);

  constructor(private _markerManager: ExgMapMarkerManagerService) {
    this._id = (markerId++).toString();
  }

  /* @internal */
  ngAfterContentInit(): void {
    this.handleInfoWindowUpdate();
    this.infoWindow.changes.subscribe(() => this.handleInfoWindowUpdate());
  }

  private handleInfoWindowUpdate(): void {
    if (this.infoWindow.length > 1) {
      throw new Error('Expected no more than one info window.');
    }
    this.infoWindow.forEach(marker => {
      marker.hostMarker = this;
    });
  }

  /** @internal */
  ngOnChanges(changes: { [key: string]: SimpleChange }): void {
    if (typeof this.latitude === 'string') {
      this.latitude = Number(this.latitude);
    }
    if (typeof this.longitude === 'string') {
      this.longitude = Number(this.longitude);
    }
    if (typeof this.latitude !== 'number' || typeof this.longitude !== 'number') {
      return;
    }
    if (!this._markerAddedToManger) {
      this._markerManager.addMarker(this);
      this._updateFitBoundsDetails();
      this._markerAddedToManger = true;
      this._addEventListeners();
      return;
    }
    // tslint:disable: no-string-literal
    if (changes['latitude'] || changes['longitude']) {
      this._markerManager.updateMarkerPosition(this);
      this._updateFitBoundsDetails();
    }
    if (changes['title']) {
      this._markerManager.updateTitle(this);
    }
    if (changes['label']) {
      this._markerManager.updateLabel(this);
    }
    if (changes['draggable']) {
      this._markerManager.updateDraggable(this);
    }
    if (changes['iconUrl']) {
      this._markerManager.updateIcon(this);
    }
    if (changes['opacity']) {
      this._markerManager.updateOpacity(this);
    }
    if (changes['visible']) {
      this._markerManager.updateVisible(this);
    }
    if (changes['zIndex']) {
      this._markerManager.updateZIndex(this);
    }
    if (changes['clickable']) {
      this._markerManager.updateClickable(this);
    }
    if (changes['animation']) {
      this._markerManager.updateAnimation(this);
    }
    // tslint:enable: no-string-literal

  }

  /** @internal */
  getFitBoundsDetails$(): Observable<ExgMapFitBoundsDetails> {
    return this._fitBoundsDetails$.asObservable();
  }

  protected _updateFitBoundsDetails(): void {
    this._fitBoundsDetails$.next({latLng: {lat: this.latitude as number, lng: this.longitude as number}});
  }

  private _addEventListeners(): void {
    const cs = this._markerManager.createEventObservable('click', this).subscribe(() => {
      if (this.openInfoWindow) {
        this.infoWindow.forEach(infoWindow => {
          infoWindow.open();
        });
      }
      this.markerClick.emit(this);
    });
    this._observableSubscriptions.push(cs);

    const dcs = this._markerManager.createEventObservable('dblclick', this).subscribe(() => {
      this.markerDblClick.emit(null);
    });
    this._observableSubscriptions.push(dcs);

    const rc = this._markerManager.createEventObservable('rightclick', this).subscribe(() => {
      this.markerRightClick.emit(null);
    });
    this._observableSubscriptions.push(rc);

    const ds =
      this._markerManager.createEventObservable<google.maps.MapMouseEvent>('dragstart', this)
        .subscribe((e: any) => this.dragStart.emit(e));
    this._observableSubscriptions.push(ds);

    const d =
      this._markerManager.createEventObservable<google.maps.MapMouseEvent>('drag', this)
        .subscribe((e: any) => this.drag.emit(e));
    this._observableSubscriptions.push(d);

    const de =
      this._markerManager.createEventObservable<google.maps.MapMouseEvent>('dragend', this)
        .subscribe((e: any) => this.dragEnd.emit(e));
    this._observableSubscriptions.push(de);

    const mover =
      this._markerManager.createEventObservable<google.maps.MapMouseEvent>('mouseover', this)
        .subscribe((e: any) => this.mouseOver.emit(e));
    this._observableSubscriptions.push(mover);

    const mout =
      this._markerManager.createEventObservable<google.maps.MapMouseEvent>('mouseout', this)
        .subscribe((e: any) => this.mouseOut.emit(e));
    this._observableSubscriptions.push(mout);

    const anChng =
      this._markerManager.createEventObservable<void>('animation_changed', this)
        .subscribe(() => {
          this.animationChange.emit(this.animation);
        });
    this._observableSubscriptions.push(anChng);
  }

  /** @internal */
  id(): string {
    return this._id;
  }

  /** @internal */
  toString(): string {
    return 'ExgMapMarker-' + this._id.toString();
  }

  /** @internal */
  ngOnDestroy(): void {
    this._markerManager.deleteMarker(this);
    // unsubscribe all registered observable subscriptions
    this._observableSubscriptions.forEach((s) => s.unsubscribe());
  }
}
